<template>
    <div class="mt-2">
        <div :class="`${scopeId}`">
            <component :is="`protokol-component-app-template-${validEntrypointTag}`" v-on="$listeners" v-bind="$attrs"
                v-if="ready">
                <slot v-for="(_, name) in $slots" :name="name" :slot="name"></slot>
                <template v-for="(_, name) in $scopedSlots" :slot="name" slot-scope="slotData">
                    <slot :name="name" v-bind="slotData" />
                </template>
            </component>
        </div>

        <b-alert :show="errorModal.show" variant="danger">
            {{ errorModal.msg }}
        </b-alert>
    </div>
</template>

<script>
import { 
    Thunder,
    Component,
    Project,
    Integrations,
    Ratchet,
    Sandbox,
    Functions
} from '@protokol/platform-libs'

import printJS from 'print-js'
import axios from 'axios'
import moment from 'moment'
import Vue from "vue"
import VueRouter from 'vue-router'
import * as CoreUI from '@/components/ui/vue/src'
import Compiler from '@/components/renderer'
import Builder from '@/components/renderer/builder'
import SVG from '@/components/renderer/svg'
import { Howl } from 'howler'
import pdfobject from 'pdfobject'

// adapters
import PlatformAdapter from '@/lib/adapters/platform'

import TemplateComponents from '../templates'

export default {
    name: 'ComponentApp',
    components: {},
    props: {
        id: {
            type: String,
            default: ''
        },
        entrypoint: {
            type: String,
            default: "index"
        },
        nodes: {
            type: Array
        },
        noRouter: {
            type: Boolean,
            default: false
        },
        dontShowErrorFeedback: {
            type: Boolean,
            default: false
        }
    },

    data: function () {
        return {
            ready: false,
            context: {
                vars: {}
            },
            errorModal: {
                show: false,
                msg: null
            },
            modules: {},
            exports: {},
            components: {}
        }
    },

    methods: {
        showErrorModal(msg) {
            if (this.dontShowErrorFeedback) {
                return
            }

            this.errorModal.show = true;
            this.errorModal.msg = msg;
        },

        find(collection, f) {
            return this.$store.getters.coldStorageFind(collection, f)
        },

        getStringBetween(str, start, end) {
            const result = str.match(new RegExp("(?:\\" + start + ")(.*)(?=\\" + end + ")", 'gs'));

            if (result === null) {
                return ""
            }

            return result[0].replace(start, "");
        },

        import(path) {
            return this.require(path)
        },

        require(path, options) {
            // get extension
            let parts = path.split(".")
            let ext = parts[parts.length - 1]
            let node = this.modules[path] || null
            if (!node) {
                if (options && !options.ignorePathError) {
                    this.showErrorModal("Can't resolve path (" + path + ") to import module.")
                }

                return;
            }

            if (ext == 'js') {
                let m = this.compile(node).default
                if (m && m.stack && m.message) {
                    throw new Error(m.message)
                }

                return m
            }

            if (ext == 'vue') {
                return () => ({
                    component: this.register(node),
                })
            }

            if (ext == 'css') {
                this.parseStyles(node)
            }

            if (ext == 'svg') {
                let svg = new SVG(node)
                return svg.transpile()
            }

            if (ext == 'json') {
                return JSON.parse(node)
            }
        },

        async register(node) {
            try {
                let compiler = new Compiler();
                let result = compiler.evaluateJS(node.script, this.libs()).default || {}
                if (result && result.stack && result.message) {
                    throw new Error(result.message)
                }

                result.template = node.template

                result.components = {
                    ...result.components,
                    ...CoreUI,
                    ...this.components,
                    ...TemplateComponents,
                }

                // scope styles only to the component
                this.parseStyles(node.style)

                return result
            } catch (err) {
                console.error(err)
                this.showErrorModal(err.message)
            }
        },

        async parseStyles(content) {
            // Create a new <style> element
            const style = document.createElement('style');

            // Define your CSS as a string
            const css = content;
            style.id = this.id;
            // Set the content of the <style> element
            style.textContent = css;

            // Append the <style> element to the <head> or <body>
            document.head.appendChild(style);
        },

        getTmpl(c) {
            try {
                return this.getStringBetween(c, "<tmpl>", "</tmpl>");
            } catch (err) {
                throw new Error("Failed to parse template:" + err.message)
            }
        },

        async loadNodes() {
            const builder = new Builder()
            this.modules = await builder.build(this.nodes)
        },

        displayError(message) {
            this.$bvToast.toast(message, {
                variant: 'danger',
                title: "Error"
            })
        },

        goToEditor() {
            this.$emit('goToEditor')
        },

        compile(expression) {
            const compiler = new Compiler()
            const code = compiler.compileJS(expression)

            return compiler.evaluateJS(code, this.libs())
        },

        libs() {
            return {
                session: this.$store.getters.getUser,
                resource: new Thunder(new PlatformAdapter(this), true),
                thunder: new Thunder(new PlatformAdapter(this), true),
                component: (new Component(new PlatformAdapter(this), 'platform')),
                integrations: new Integrations(new PlatformAdapter(this)),
                ratchet: new Ratchet(new PlatformAdapter(this)),
                sandbox: new Sandbox(new PlatformAdapter(this)),
                project: new Project(new PlatformAdapter(this)),
                functions: new Functions(new PlatformAdapter(this)),
                register: this.register,
                require: this.require,
                print: printJS,
                axios: axios,
                moment: moment,
                lodash: _,

                log: {
                    info: (...args) => {
                        console.log(...args)
                    },

                    error: (err) => {
                        console.error(err)
                    }
                },

                app: {
                    showLoadingPopup: (t) => this.$loadingPopup(t),
                    store: (collection, data) => { this.$store.commit("COLD_STORAGE_STORE", { collection: collection, data: data }) },
                    find: (collection, f) => { return this.$store.getters.coldStorageFind(collection, f) },
                    openWindow: (link, target) => {
                        window.open(link, target)
                    },
                    getEnv: (key, def) => {
                        let config = this.require('config.js', { ignorePathErrors: true })
                        let currentEnv = this.$store.getters.getCurrentEnv

                        return _.get(config.env, `${currentEnv}.${key}`, def)
                    },
                    notify: (type, text) => {

                        this.$bvToast.toast(text, {
                            title: (type === 'success' ? 'Success' : (type === 'error' ? 'Error' : type)),
                            variant: type,
                            position: 'top-right',
                        });
                    },

                    interval: {
                        set: (fn, tm) => {
                            return window.setInterval(function () {
                                fn()
                            }, tm)
                        },

                        clear: (interval) => {
                            window.clearInterval(interval)
                        }
                    },

                    audio: {
                        play: (file, options) => {
                            var sound = new Howl({
                                src: [file],
                                volume: 1,
                                ...options
                            });

                            sound.play();

                            return sound
                        },

                        playNotification: (options) => {
                            var sound = new Howl({
                                src: ['../sound/notification.wav'],
                                volume: 1,
                                ...options
                            });

                            sound.play();

                            return sound
                        }
                    },

                    eval(expr, params) {
                        let keys = Object.keys(params)
                        let values = Object.values(params)

                        let result = new Function(...keys, `try{ ${expr} } catch (err) { return err; }`)

                        return result(...values)
                    },
                },

                user: {
                    roles: async () => {
                        return this.$store.dispatch('fetchRoles')
                    },

                    can: (action) => {
                        return this.$can(action)
                    }
                },

                event: {
                    listen(e, fn) {
                        window.addEventListener(e, (ev) => {
                            fn(ev)
                        })
                    }
                },

                loader: {
                    loadSVG: (path, name) => {
                        let content = this.require(path)
                        this.components[name] = Vue.extend(content)
                    }
                },

                file: {
                    readAsDataUrl: (file, options) => {
                        return new Promise((resolve, reject) => {
                            var reader = new FileReader()
                            reader.readAsDataURL(file, options)
                            reader.onload = () => resolve(reader.result);
                            reader.onerror = error => reject(error);
                        })
                    },

                    pdf: pdfobject
                },

                voyager: {
                    async call(fname, ...args) {
                        if (!window.flutter_inappwebview) {
                            throw new Error("invalid platform");
                        }

                        return await window.flutter_inappwebview.callHandler(fname, ...args);
                    }
                },

                eventbus: this.$eventBus,

                exports: {}
            }
        },

        getContents(template) {
            try {
                let parser = new DOMParser()
                let doc = parser.parseFromString(template, "text/html")

                return {
                    template: this.getTmpl(template),
                    style: _.get(doc.querySelector("style"), 'textContent', ''),
                    script: _.get(doc.querySelector("script"), 'textContent', '')
                }
            } catch (err) {
                this.$debugLog(err)
                this.showErrorModal("Failed to render template: " + err.message);
            }
        }
    },

    computed: {
        template() {
            try {
                const path = this.entrypoint + ".vue"

                return this.modules[path]
            } catch (err) {
                this.$debugLog(err)
                this.showErrorModal(`Did you properly formated your ${this.entrypoint} template?`);
            }
        },

        scopeId() {
            return this.id.replace(/[0-9]/g, '').replaceAll('-', '');
        },

        validEntrypointTag() {
            return this.entrypoint.replaceAll("/", "-")
        }

    },

    destroyed() {
        this.$eventBus.close()
        document.querySelectorAll(`style#${CSS.escape(this.id)}`).forEach(style => style.remove())
    },

    mounted() { },

    async created() {
        this.$eventBus.connect()
        await this.loadNodes()

        let compiler = new Compiler();

        const { template, script, style } = this.template || {}
        let result = compiler.evaluateJS(script, this.libs()).default || {}

        if (result && result.stack && result.message) {
            this.showErrorModal(result.message)
            return
        }

        try {
            this.parseStyles(style)

            result.components = {
                ...result.components,
                ...CoreUI,
                ...this.components,
                ...TemplateComponents,
            }

            let component = {
                ...result,
                template: template
            }

            if (!this.noRouter) {
                component.router = new VueRouter({
                    // 4. Provide the history implementation to use. We are using the hash history for simplicity here.
                    routes: await this.require('routes.js', { ignorePathError: true }) || [], // short for `routes: routes`
                    base: this.$route.path
                })
            }

            Vue.component('protokol-component-app-template-' + this.validEntrypointTag, component)

            this.ready = true
        } catch (err) {
            console.error(err)
        }
    },

    beforeDestroy() {
        // Get a reference to the last interval + 1
        const interval_id = window.setInterval(function () { }, Number.MAX_SAFE_INTEGER);

        // Clear any timeout/interval up to that id
        for (let i = 1; i < interval_id; i++) {
            window.clearInterval(i);
        }
    },

    updated() {
        console.log("[Render] LegacyComponentApp rendered")
    },
}
</script>